import { useEffect, useRef, useState } from "react";
import { fetchPrivateVenueContact, savePrivateVenueContact, removePrivateVenueContact, type PrivateVenueContact, type PrivateVenueContactInput } from "@/api/discover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const EMPTY: PrivateVenueContactInput = { name: null, role: null, email: null, phone: null, notes: null };
type Props = { artistId: string; globalVenueId: string; initialContact?: PrivateVenueContact; onSaved?: (contact: PrivateVenueContact) => void };

export default function PrivateVenueContactEditor({ artistId, globalVenueId, initialContact, onSaved }: Props) {
  const identity = useRef("");
  const alive = useRef(true);
  const [contact, setContact] = useState<PrivateVenueContact | null>(initialContact ?? null);
  const [form, setForm] = useState<PrivateVenueContactInput>(EMPTY);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    const key = `${artistId}:${globalVenueId}`;
    alive.current = true;
    if (identity.current !== key) { setEditing(false); setMessage(""); setError(""); }
    identity.current = key;
    setContact(initialContact ?? null);
    let cancelled = false;
    if (initialContact) return () => { alive.current = false; };
    fetchPrivateVenueContact(globalVenueId, artistId)
      .then(result => { if (!cancelled) setContact(result.private_contact); })
      .catch(() => { if (!cancelled) setError("Your contact couldn't be loaded."); });
    return () => { cancelled = true; alive.current = false; };
  }, [artistId, globalVenueId, initialContact]);

  const edit = () => { setForm(contact ? { name: contact.name, role: contact.role, email: contact.email, phone: contact.phone, notes: contact.notes } : EMPTY); setEditing(true); setMessage(""); setError(""); };
  const save = async (remove = false) => {
    const key = `${artistId}:${globalVenueId}`;
    setBusy(true); setError(""); setMessage("");
    try {
      const result = remove ? await removePrivateVenueContact(globalVenueId, artistId) : await savePrivateVenueContact(globalVenueId, form, artistId);
      if (!alive.current || identity.current !== key) return;
      setContact(result.private_contact); setEditing(false); onSaved?.(result.private_contact); setMessage(remove ? "Contact removed" : "Saved");
    } catch { if (alive.current && identity.current === key) setError("Your contact couldn't be saved. Your changes are still here."); }
    finally { setBusy(false); }
  };
  return (
    <section aria-label="Your contact" className="mb-4 space-y-2 rounded-lg border p-3 text-sm">
      <h3 className="font-semibold">Your contact</h3>
      <p className="text-xs text-muted-foreground">Private to your organization · for this artist’s venue relationship</p>
      {contact?.has_details && !editing && <div className="space-y-1">
        {(contact.name || contact.role) && <p>{[contact.name, contact.role].filter(Boolean).join(" · ")}</p>}
        {contact.email && <p><a href={`mailto:${contact.email}`} className="text-primary">{contact.email}</a></p>}
        {contact.phone && <p>{contact.phone}</p>}
        {contact.notes && <p className="whitespace-pre-wrap">{contact.notes}</p>}
      </div>}
      {editing ? <form className="space-y-2" onSubmit={event => { event.preventDefault(); void save(); }}>
        {([['name', 'Contact name', 255], ['role', 'Role / title', 64], ['email', 'Contact email', 255], ['phone', 'Contact phone', 64]] as const).map(([key, label, maxLength]) => <label key={key} className="block">{label}<Input aria-label={label} type={key === 'email' ? 'email' : 'text'} maxLength={maxLength} value={form[key] ?? ''} onChange={event => { setForm(current => ({ ...current, [key]: event.target.value })); setMessage(''); }} /></label>)}
        <label className="block">Relationship notes<Textarea aria-label="Relationship notes" maxLength={4096} value={form.notes ?? ''} onChange={event => setForm(current => ({ ...current, notes: event.target.value }))} /></label>
        <Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save contact'}</Button>{' '}
        <Button type="button" variant="ghost" disabled={busy} onClick={() => setEditing(false)}>Cancel</Button>
      </form> : <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={edit} disabled={!contact || busy}>{contact?.has_details ? 'Edit your contact' : '+ Add your contact'}</Button>
        {contact?.has_details && <Button type="button" variant="ghost" disabled={busy} onClick={() => void save(true)}>Remove contact</Button>}
      </div>}
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
